import { useEffect, useState } from "react";
import { bdcApi } from "../services/bdcApi";

/**
 * useCommissionConfig — reads GET /admin/commission-config once per session and
 * hands back the commission families so dashboards can render their cut labels
 * from live config instead of hardcoded 30% / 15% / 5% numbers.
 *
 * The endpoint is being built in parallel on the API side, so this hook is
 * written to FAIL SOFT: while it 404s (or errors for any reason) `config`
 * stays null and callers must OMIT the percentage rather than fall back to a
 * stale hardcoded value that could mislead a contractor about their pay.
 *
 * Cached at module scope (a single in-flight promise) so every dashboard that
 * mounts in a session shares one network round-trip.
 */
/**
 * formatCommissionPct — normalize a commission-config `pct` (which may arrive
 * as a fraction like 0.30 or a whole percent like 30) to a display string.
 * Returns null when the value is missing so callers OMIT the number rather
 * than print a misleading "0%".
 */
export function formatCommissionPct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const n = Number(pct);
  const asPct = n > 0 && n <= 1 ? n * 100 : n;
  return `${Number(asPct.toFixed(2))}%`;
}

let cachedPromise = null;

function loadConfig() {
  if (!cachedPromise) {
    cachedPromise = bdcApi
      .commissionConfig()
      .then((res) => {
        const data = res?.data;
        // Feature-detect on shape: a real config carries the family objects.
        if (data && (data.bc_direct || data.consultant_direct || data.bc_override || data.deal)) {
          return data;
        }
        return null;
      })
      .catch(() => null);
  }
  return cachedPromise;
}

/**
 * Test / logout seam — drop the cached promise so a fresh session re-fetches.
 */
export function resetCommissionConfigCache() {
  cachedPromise = null;
}

export function useCommissionConfig() {
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    loadConfig().then((cfg) => {
      if (!live) return;
      setConfig(cfg);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, []);

  return { config, loaded };
}

export default useCommissionConfig;
